import { tokens } from '@/lib/tokens.js';
import { useState } from 'react';
import { useAdminAuditLog } from '../../hooks/use-admin-audit-log.js';
import type { AuditLogParams } from '../../lib/admin-client.js';
import { AuditLogRow } from './audit-log-row.js';

function defaultFrom(): string {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().split('T')[0] ?? '';
}

function defaultTo(): string {
  return new Date().toISOString().split('T')[0] ?? '';
}

const s = {
  container: {
    backgroundColor: tokens.colors.bgCardDark,
    borderRadius: tokens.radius.card,
    overflow: 'hidden',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,.1)',
  },
  header: {
    padding: '1rem 1.5rem',
    borderBottom: `1px solid ${tokens.colors.borderDark}`,
  },
  title: { fontSize: '1rem', fontWeight: 600, color: tokens.colors.textDark, margin: 0 },
  filters: {
    padding: '0.75rem 1.5rem',
    borderBottom: `1px solid ${tokens.colors.borderDark}`,
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap' as const,
    alignItems: 'flex-end',
  },
  filterGroup: { display: 'flex', flexDirection: 'column' as const, gap: '0.25rem' },
  filterLabel: { fontSize: '0.75rem', color: tokens.colors.textMutedDark },
  input: {
    backgroundColor: '#0f172a',
    border: `1px solid ${tokens.colors.borderDark}`,
    borderRadius: tokens.radius.input,
    color: tokens.colors.textDark,
    padding: '0.375rem 0.625rem',
    fontSize: '0.875rem',
    outline: 'none',
    width: '160px',
  },
  table: { width: '100%', borderCollapse: 'collapse' as const },
  th: {
    padding: '0.625rem 0.75rem',
    textAlign: 'left' as const,
    fontSize: '0.75rem',
    fontWeight: 600,
    color: tokens.colors.textMutedDark,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    borderBottom: `1px solid ${tokens.colors.borderDark}`,
    backgroundColor: tokens.colors.bgCardDark,
    position: 'sticky' as const,
    top: 0,
  },
  pagination: {
    padding: '0.75rem 1.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTop: `1px solid ${tokens.colors.borderDark}`,
    fontSize: '0.875rem',
    color: tokens.colors.textMutedDark,
  },
  btn: {
    backgroundColor: 'transparent',
    border: `1px solid ${tokens.colors.borderDark}`,
    borderRadius: tokens.radius.button,
    color: tokens.colors.textDark,
    padding: '0.25rem 0.625rem',
    fontSize: '0.875rem',
    cursor: 'pointer',
  },
};

interface AuditLogPageProps {
  tenantId: string;
}

export function AuditLogPage({ tenantId }: AuditLogPageProps) {
  const [params, setParams] = useState<AuditLogParams>({
    from: defaultFrom(),
    to: defaultTo(),
    'page-number': 1,
    'page-size': 50,
  });

  const { data, isLoading, isError } = useAdminAuditLog(tenantId, params);

  function handleFilter(key: keyof AuditLogParams, value: string) {
    setParams((prev) => ({ ...prev, [key]: value || undefined, 'page-number': 1 }));
  }

  const items = data?.items ?? [];
  const pagination = data?.pagination;

  return (
    <div style={s.container}>
      <div style={s.header}>
        <h2 style={s.title}>Audit Log</h2>
      </div>
      <div style={s.filters}>
        <div style={s.filterGroup}>
          <span style={s.filterLabel}>From</span>
          <input
            type="date"
            style={s.input}
            value={params.from ?? ''}
            onChange={(e) => handleFilter('from', e.target.value)}
            aria-label="Filter from date"
          />
        </div>
        <div style={s.filterGroup}>
          <span style={s.filterLabel}>To</span>
          <input
            type="date"
            style={s.input}
            value={params.to ?? ''}
            onChange={(e) => handleFilter('to', e.target.value)}
            aria-label="Filter to date"
          />
        </div>
        <div style={s.filterGroup}>
          <span style={s.filterLabel}>Actor</span>
          <input
            style={s.input}
            placeholder="Actor user ID…"
            onChange={(e) => handleFilter('actor_user_id', e.target.value)}
            aria-label="Filter by actor user ID"
          />
        </div>
        <div style={s.filterGroup}>
          <span style={s.filterLabel}>Action</span>
          <input
            style={s.input}
            placeholder="e.g. grant.upsert"
            onChange={(e) => handleFilter('action', e.target.value)}
            aria-label="Filter by action"
          />
        </div>
        <div style={s.filterGroup}>
          <span style={s.filterLabel}>Resource type</span>
          <input
            style={s.input}
            placeholder="e.g. role"
            onChange={(e) => handleFilter('resource_type', e.target.value)}
            aria-label="Filter by resource type"
          />
        </div>
        <div style={s.filterGroup}>
          <span style={s.filterLabel}>Resource ID</span>
          <input
            style={s.input}
            placeholder="ID…"
            onChange={(e) => handleFilter('resource_id', e.target.value)}
            aria-label="Filter by resource ID"
          />
        </div>
      </div>

      {isLoading ? (
        <output
          style={{
            display: 'block',
            padding: '2rem',
            textAlign: 'center',
            color: tokens.colors.textMutedDark,
          }}
          aria-label="Loading audit log"
        >
          Loading…
        </output>
      ) : isError ? (
        <div style={{ padding: '2rem', color: tokens.colors.error }} role="alert">
          Error loading audit log
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={s.table} aria-label="Audit log">
            <thead>
              <tr>
                <th style={s.th}>Time</th>
                <th style={s.th}>Actor</th>
                <th style={s.th}>Action</th>
                <th style={s.th}>Resource type</th>
                <th style={s.th}>Resource ID</th>
                <th style={s.th} />
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      padding: '2rem',
                      textAlign: 'center',
                      color: tokens.colors.textMutedDark,
                      fontSize: '0.875rem',
                    }}
                  >
                    No audit log entries found
                  </td>
                </tr>
              ) : (
                items.map((entry, idx) => (
                  <AuditLogRow key={entry.id} entry={entry} isOdd={idx % 2 === 1} />
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {pagination && (pagination.total_pages ?? 1) > 1 && (
        <div style={s.pagination}>
          <span>
            Page {pagination.page_number} of {pagination.total_pages} ({pagination.total_items}{' '}
            entries)
          </span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              type="button"
              style={s.btn}
              disabled={(params['page-number'] ?? 1) <= 1}
              onClick={() =>
                setParams((p) => ({ ...p, 'page-number': (p['page-number'] ?? 1) - 1 }))
              }
              aria-label="Previous page"
            >
              Prev
            </button>
            <button
              type="button"
              style={s.btn}
              disabled={(params['page-number'] ?? 1) >= (pagination.total_pages ?? 1)}
              onClick={() =>
                setParams((p) => ({ ...p, 'page-number': (p['page-number'] ?? 1) + 1 }))
              }
              aria-label="Next page"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
